import React from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import override from "../../../assets/override_png.png";
import over from "../../../assets/over_svg.svg";
import pay from "../../../assets/rupee_icon.svg";

interface ActionButtonProps {
  onClick?: () => void; // Make onClick optional
  icon?: string | React.ReactNode; // Optional Font Awesome icon class
  tooltipText?: string; // Optional text for the tooltip
  disabled?: boolean; // Optional prop to disable the button
  className?: string;
  color?: string;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  onClick,
  icon,
  tooltipText,
  disabled = false,
  color = "gray", // Default color
}) => {
  const getButtonColor = () => {
    if (disabled) {
      return "bg-gray-400 text-gray-600";
    }
    switch (color) {
      case "blue":
        return "bg-[#CBDCF9] hover:bg-[#103680]";
      case "green":
        return "bg-[#C9E8E8] hover:bg-[#105352]";
      case "red":
        return "bg-[#F3D4D1] hover:bg-[#70241D]";
      case "yellow":
        return "bg-[#FFF3DD] hover:bg-[#FFE0B0]";
      case "light":
        return "bg-[#C0F1FF] hover:bg-[#2F9AB8]";
      case "cross":
        return "bg-[#c7c6c6] hover:bg-[#56BD27]";
      default:
        return "bg-gray-100 hover:bg-gray-300 text-gray-600";
    }
  };

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={`tooltip-${tooltipText}`}>{tooltipText}</Tooltip>}
    >
      <div className="relative inline-flex">
        <button
          onClick={!disabled && onClick ? onClick : undefined}
          className={`p-3 rounded-md focus:outline-none transition-colors duration-200 ease-in-out
                        ${getButtonColor()}
                        ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          disabled={disabled}
        >
          {typeof icon === "string" ? (
            icon.startsWith("fa-") ? (
              <i className={`fa ${icon} m-0`}></i>
            ) : (
              <img src={icon} alt={tooltipText} className="w-4 h-4" />
            )
          ) : (
            icon
          )}
        </button>
      </div>
    </OverlayTrigger>
  );
};

// EditButton Component
export const EditButton: React.FC<ActionButtonProps> = ({
  onClick,
  icon = "fa-check-square-o",
  tooltipText = "Edit",
  disabled = false,
}) => (
  <ActionButton
    onClick={onClick}
    icon={icon}
    tooltipText={tooltipText}
    disabled={disabled}
    color="blue"
  />
);

// ViewButton Component
export const ViewButton: React.FC<
  ActionButtonProps & {
    setDocument?: (doc: { url: string; name: string }) => void;
    documentUrl?: string;
    documentName?: string;
  }
> = ({
  onClick,
  icon = "fa-eye",
  tooltipText = "View",
  disabled = false,
  setDocument,
  documentUrl,
  documentName,
}) => (
  <ActionButton
    onClick={() => {
      if (setDocument && documentUrl && documentName) {
        setDocument({
          url: documentUrl,
          name: documentName,
        });
      }
      if (onClick) onClick();
    }}
    icon={icon}
    tooltipText={tooltipText}
    disabled={disabled}
    color="green"
  />
);

// DeleteButton Component
export const DeleteButton: React.FC<ActionButtonProps> = ({
  onClick,
  icon = "fa-trash-o",
  tooltipText = "Delete",
  disabled = false,
}) => (
  <ActionButton
    onClick={onClick}
    icon={icon}
    tooltipText={tooltipText}
    disabled={disabled}
    color="red"
  />
);

// OverrideButton Component
export const OverrideButton: React.FC<ActionButtonProps> = ({
  onClick,
  // icon = { override },
  tooltipText = "Override",
  disabled = false,
}) => (
  <ActionButton
    onClick={onClick}
    icon={over}
    tooltipText={tooltipText}
    disabled={disabled}
    color="blue"
  />
);

// PayButton Component
export const PayButton: React.FC<ActionButtonProps> = ({
  onClick,
  tooltipText = "Pay",
  disabled = false,
}) => (
  <ActionButton
    onClick={onClick}
    icon={pay}
    tooltipText={tooltipText}
    disabled={disabled}
    color="blue"
  />
);

// DownloadButton Component
export const DownloadButton: React.FC<ActionButtonProps> = ({
  onClick,
  icon = "fa-download",
  tooltipText = "Download",
  disabled = false,
}) => (
  <ActionButton
    onClick={onClick}
    icon={icon}
    tooltipText={tooltipText}
    disabled={disabled}
    color="light"
  />
);

//For Level 1 for NOC
interface ToggleButtonL1Props extends ActionButtonProps {
  isChecked: boolean;
  isUpdating: boolean;
  statusCheck: string;
}

export const NocL1Button: React.FC<ToggleButtonL1Props> = ({
  onClick,
  isChecked = true, // Set default to true
  isUpdating,
  statusCheck,
  tooltipText = "Toggle",
  disabled = false,
}) => {
  const icon = isChecked ? (
    <i className="m-0 fa fa-check-square-o text-green-400"></i>
  ) : (
    <i className="m-0 fa fa-window-close-o text-red-500"></i>
  );
  return (
    <ActionButton
      onClick={onClick}
      icon={icon}
      tooltipText={tooltipText}
      disabled={
        disabled || isUpdating || statusCheck !== "Pending Level 1 Review"
      }
      color={isChecked ? "green" : "red"} // Change 'cross' to 'green'
    />
  );
};

// For Level 1 Doc Desicion
interface DocDecisionButtonL1Props {
  onClick: () => void;
  isChecked: boolean;
  isUpdating: boolean;
  statusCheck: string;
  tooltipText?: string;
  disabled?: boolean;
}

export const DocDecisionL1Button: React.FC<DocDecisionButtonL1Props> = ({
  onClick,
  isChecked = true,
  isUpdating,
  statusCheck,
  tooltipText = "Toggle Document Decision",
  disabled = false,
}) => {
  const icon = isChecked ? (
    <i className="m-0 fa fa-check-square-o text-green-400"></i>
  ) : (
    <i className="m-0 fa fa-window-close-o text-red-500"></i>
  );

  return (
    <ActionButton
      onClick={onClick}
      icon={icon}
      tooltipText={tooltipText}
      disabled={
        disabled || isUpdating || statusCheck !== "Pending Level 1 Review"
      }
      color={isChecked ? "green" : "red"}
      className="text-lg"
    />
  );
};

/**
 * Status the Level 2 NOC / document decision toggles are enabled in when a
 * caller does not say otherwise.
 */
const LEVEL_2_DECISION_STATUSES = ["Pending Level 2 Review"];

// Add this new interface for ToggleButtonProps for Level 2
interface ToggleButtonProps extends ActionButtonProps {
  isChecked: boolean;
  isUpdating: boolean;
  statusCheck: string;
  /**
   * Statuses in which the toggle can be clicked. Defaults to "Pending Level 2
   * Review" only. The Final Review screens pass their own status for an
   * Offline Tentative application, the one case where a document or NOC can
   * still be sent back to the applicant at that stage.
   */
  enabledForStatuses?: string[];
}

export const ToggleButton: React.FC<ToggleButtonProps> = ({
  onClick,
  isChecked = true, // Set default to true
  isUpdating,
  statusCheck,
  tooltipText = "Toggle",
  disabled = false,
  enabledForStatuses = LEVEL_2_DECISION_STATUSES,
}) => {
  const icon = isChecked ? (
    <i className="m-0 fa fa-check-square-o text-green-400"></i>
  ) : (
    <i className="m-0 fa fa-window-close-o text-red-500"></i>
  );
  return (
    <ActionButton
      onClick={onClick}
      icon={icon}
      tooltipText={tooltipText}
      /**
       * BEFORE - enabled in one fixed status:
       * disabled={
       *   disabled || isUpdating || statusCheck !== "Pending Level 2 Review"
       * }
       */
      disabled={
        disabled || isUpdating || !enabledForStatuses.includes(statusCheck)
      }
      color={isChecked ? "green" : "red"} // Change 'cross' to 'green'
    />
  );
};

// For Level 2 Doc Desicion
interface DocDecisionButtonProps {
  onClick: () => void;
  isChecked: boolean;
  isUpdating: boolean;
  statusCheck: string;
  tooltipText?: string;
  disabled?: boolean;
  /** Same meaning as on ToggleButton above. */
  enabledForStatuses?: string[];
}

export const DocDecisionButton: React.FC<DocDecisionButtonProps> = ({
  onClick,
  isChecked = true,
  isUpdating,
  statusCheck,
  tooltipText = "Toggle Document Decision",
  disabled = false,
  enabledForStatuses = LEVEL_2_DECISION_STATUSES,
}) => {
  const icon = isChecked ? (
    <i className="m-0 fa fa-check-square-o text-green-400"></i>
  ) : (
    <i className="m-0 fa fa-window-close-o text-red-500"></i>
  );

  return (
    <ActionButton
      onClick={onClick}
      icon={icon}
      tooltipText={tooltipText}
      /**
       * BEFORE - enabled in one fixed status:
       * disabled={
       *   disabled || isUpdating || statusCheck !== "Pending Level 2 Review"
       * }
       */
      disabled={
        disabled || isUpdating || !enabledForStatuses.includes(statusCheck)
      }
      color={isChecked ? "green" : "red"}
      className="text-lg"
    />
  );
};

//For Level 3 for NOC
interface ToggleButtonL3Props extends ActionButtonProps {
  isChecked: boolean;
  isUpdating: boolean;
  statusCheck: string;
}

export const NocButton: React.FC<ToggleButtonL3Props> = ({
  onClick,
  isChecked = true, // Set default to true
  isUpdating,
  statusCheck,
  tooltipText = "Toggle",
  disabled = false,
}) => {
  const icon = isChecked ? (
    <i className="m-0 fa fa-check-square-o text-green-400"></i>
  ) : (
    <i className="m-0 fa fa-window-close-o text-red-500"></i>
  );
  return (
    <ActionButton
      onClick={onClick}
      icon={icon}
      tooltipText={tooltipText}
      disabled={
        disabled ||
        isUpdating ||
        (statusCheck !== "Pending Level 3 Review" &&
          statusCheck !== "Pending Rejection Confirmation")
      }
      color={isChecked ? "green" : "red"} // Change 'cross' to 'green'
    />
  );
};

// For Level 3 Doc Desicion
interface DocDecisionButtonL3Props {
  onClick: () => void;
  isChecked: boolean;
  isUpdating: boolean;
  statusCheck: string;
  tooltipText?: string;
  disabled?: boolean;
}

export const DocDecisionL3Button: React.FC<DocDecisionButtonL3Props> = ({
  onClick,
  isChecked = true,
  isUpdating,
  statusCheck,
  tooltipText = "Toggle Document Decision",
  disabled = false,
}) => {
  const icon = isChecked ? (
    <i className="m-0 fa fa-check-square-o text-green-400"></i>
  ) : (
    <i className="m-0 fa fa-window-close-o text-red-500"></i>
  );

  return (
    <ActionButton
      onClick={onClick}
      icon={icon}
      tooltipText={tooltipText}
      disabled={
        disabled ||
        isUpdating ||
        (statusCheck !== "Pending Level 3 Review" &&
          statusCheck !== "Pending Rejection Confirmation")
      }
      color={isChecked ? "green" : "red"}
      className="text-lg"
    />
  );
};

// For Payment Check Button
interface PaymentStatusButtonProps {
  onClick: () => void;
  paymentStatus: string;
  disabled?: boolean;
  tooltipText?: string;
}

export const PaymentStatusButton: React.FC<PaymentStatusButtonProps> = ({
  onClick,
  paymentStatus,
  disabled = false,
  tooltipText = "Toggle Payment Status",
}) => {
  const isDiscrepancy = paymentStatus === "Payment Discrepancy";

  const icon = isDiscrepancy ? (
    <i className="m-0 fa fa-window-close-o text-red-500"></i>
  ) : (
    <i className="m-0 fa fa-check-square-o text-green-400"></i>
  );

  return (
    <ActionButton
      onClick={onClick}
      icon={icon}
      tooltipText={tooltipText}
      disabled={disabled}
      color={isDiscrepancy ? "red" : "green"}
      className="text-lg"
    />
  );
};

// For Upload Button
interface UploadButtonProps {
  onClick: () => void;
  tooltipText?: string;
  className?: string;
}

export const UploadButton: React.FC<UploadButtonProps> = ({
  onClick,
  tooltipText = "Upload Document",
  className = "text-lg mb-2 sm:mb-0 sm:mr-2",
}) => {
  return (
    <ActionButton
      onClick={onClick}
      icon={<i className="m-0 fa fa-upload"></i>}
      tooltipText={tooltipText}
      className={className}
      color="blue" // You can adjust this color as needed
    />
  );
};

export default EditButton;
